/**
 * Keyword and pattern detection for ludicrous-gateway
 * Scans request content for sensitive keywords and patterns
 */

import type { Message, ContentBlock, TextContent } from "../types/messages.js";

/**
 * Default sensitive keywords that suggest local/private routing
 */
export const DEFAULT_SENSITIVE_KEYWORDS = [
  // Secrets and credentials
  "API key",
  "api_key",
  "apikey",
  "secret",
  "password",
  "credential",
  "token",
  "auth_token",
  "access_token",
  "private_key",
  "ssh key",

  // Environment files
  ".env",
  "dotenv",
  "environment variable",

  // Personal identifiable information
  "social security",
  "SSN",
  "credit card",
  "bank account",
  "passport number",

  // Internal systems
  "internal only",
  "confidential",
  "proprietary",
  "trade secret",
];

/**
 * Keyword detector configuration
 */
export interface KeywordDetectorConfig {
  /** Keywords to detect (case-insensitive) */
  keywords: string[];
  /** Regex patterns to match */
  patterns: string[];
  /** Whether to use case-sensitive matching */
  caseSensitive?: boolean;
}

/**
 * Keyword detection result
 */
export interface KeywordDetectionResult {
  /** Whether any keywords were found */
  hasMatches: boolean;
  /** Keywords that were found */
  matchedKeywords: string[];
  /** Patterns that matched */
  matchedPatterns: string[];
  /** Whether content appears sensitive */
  isSensitive: boolean;
}

/**
 * Extract all text content from messages
 */
function extractAllText(messages: Message[], system?: string | ContentBlock[]): string {
  const parts: string[] = [];

  // Extract from system prompt
  if (system) {
    if (typeof system === "string") {
      parts.push(system);
    } else {
      for (const block of system) {
        if (block.type === "text") {
          parts.push(block.text);
        }
      }
    }
  }

  // Extract from messages
  for (const message of messages) {
    if (typeof message.content === "string") {
      parts.push(message.content);
    } else {
      for (const block of message.content) {
        if (block.type === "text") {
          parts.push(block.text);
        } else if (block.type === "tool_result") {
          if (typeof block.content === "string") {
            parts.push(block.content);
          } else {
            for (const innerBlock of block.content) {
              if (innerBlock.type === "text") {
                parts.push((innerBlock as TextContent).text);
              }
            }
          }
        }
      }
    }
  }

  return parts.join("\n");
}

/**
 * Keyword detector class
 */
export class KeywordDetector {
  private keywords: Set<string>;
  private lowercaseKeywords: Set<string>;
  private patterns: RegExp[];
  private caseSensitive: boolean;

  constructor(config: KeywordDetectorConfig) {
    this.keywords = new Set(config.keywords);
    this.lowercaseKeywords = new Set(
      config.keywords.map((k) => k.toLowerCase())
    );
    this.patterns = config.patterns.map((p) => new RegExp(p, "gi"));
    this.caseSensitive = config.caseSensitive ?? false;
  }

  /**
   * Detect keywords and patterns in text
   */
  detect(text: string): KeywordDetectionResult {
    const matchedKeywords: string[] = [];
    const matchedPatterns: string[] = [];

    // Check keywords
    const searchText = this.caseSensitive ? text : text.toLowerCase();

    for (const keyword of this.keywords) {
      const searchKeyword = this.caseSensitive
        ? keyword
        : keyword.toLowerCase();

      if (searchText.includes(searchKeyword)) {
        matchedKeywords.push(keyword);
      }
    }

    // Check patterns
    for (const pattern of this.patterns) {
      // Reset lastIndex for global regex
      pattern.lastIndex = 0;
      if (pattern.test(text)) {
        matchedPatterns.push(pattern.source);
      }
    }

    const hasMatches = matchedKeywords.length > 0 || matchedPatterns.length > 0;

    // Check for known sensitive patterns
    const isSensitive = this.checkSensitivePatterns(text) || hasMatches;

    return {
      hasMatches,
      matchedKeywords,
      matchedPatterns,
      isSensitive,
    };
  }

  /**
   * Detect keywords in message request
   */
  detectInRequest(request: {
    messages: Message[];
    system?: string | ContentBlock[];
  }): KeywordDetectionResult {
    const text = extractAllText(request.messages, request.system);
    return this.detect(text);
  }

  /**
   * Check for built-in sensitive patterns
   */
  private checkSensitivePatterns(text: string): boolean {
    // Check for API key patterns
    const apiKeyPatterns = [
      /sk-[a-zA-Z0-9]{20,}/,  // OpenAI-style
      /anthropic-[a-zA-Z0-9-]+/i,  // Anthropic-style
      /[a-zA-Z0-9_-]{32,}/, // Generic long tokens
    ];

    for (const pattern of apiKeyPatterns) {
      if (pattern.test(text)) {
        return true;
      }
    }

    // Check for environment variable assignments
    if (/[A-Z_]+=["']?[^"'\s]+["']?/.test(text)) {
      return true;
    }

    return false;
  }

  /**
   * Add additional keywords
   */
  addKeywords(keywords: string[]): void {
    for (const keyword of keywords) {
      this.keywords.add(keyword);
      this.lowercaseKeywords.add(keyword.toLowerCase());
    }
  }

  /**
   * Add additional patterns
   */
  addPatterns(patterns: string[]): void {
    for (const pattern of patterns) {
      this.patterns.push(new RegExp(pattern, "gi"));
    }
  }
}

/**
 * Create a keyword detector with default sensitive keywords
 */
export function createDefaultKeywordDetector(): KeywordDetector {
  return new KeywordDetector({
    keywords: DEFAULT_SENSITIVE_KEYWORDS,
    patterns: [],
    caseSensitive: false,
  });
}
